import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ToastMessageOptions } from 'primeng/api';
import { TelemetryService } from './telemetry.service';

/**
 * PrimeNG's MessageService, plus a report of every error toast.
 *
 * A toast is not an unhandled error — it comes from code that *caught* something,
 * so GlobalErrorHandler never sees it. Without this, "what was the user actually
 * told" is unanswerable: a backend 500 might produce any of several messages, or
 * none, and a purely client-side toast (a missing-ETag guard, a validation check)
 * touches no server at all.
 *
 * Substituted once in app.config.ts:
 *
 *     { provide: MessageService, useClass: ReportingMessageService }
 *
 * That reaches every call site without touching one of them — provided no
 * component declares `providers: [MessageService]`, which would give it a private
 * plain instance instead. Those declarations were removed for this reason.
 */
@Injectable()
export class ReportingMessageService extends MessageService {
  private readonly telemetry = inject(TelemetryService);

  override add(message: ToastMessageOptions): void {
    this.reportIfError(message);
    super.add(message);
  }

  override addAll(messages: ToastMessageOptions[]): void {
    messages?.forEach((message) => this.reportIfError(message));
    super.addAll(messages);
  }

  /**
   * A denylist, not an allowlist. Only 'success' and 'info' are confirmed
   * uninteresting; everything else means the user was told something went wrong.
   *
   * 'warn' is included on purpose. It is 107 of the 314 reported call sites, and
   * they are failures the user is blocked by, not asides — "Validation Error:
   * please fix the highlighted fields", "Already added". Reporting only 'error'
   * missed exactly the case this was built for: a user saying "it would not let
   * me save" with nothing in Loki to show for it.
   *
   * Deliberately fail-open. An allowlist would silently drop any severity added
   * later, and a toast that is never reported gives no sign it is missing —
   * unlike an unwanted one, which is visible and one line to exclude.
   */
  private static readonly IGNORED_SEVERITIES = new Set(['success', 'info']);

  private reportIfError(message: ToastMessageOptions): void {
    const severity = message?.severity ?? 'error';

    if (ReportingMessageService.IGNORED_SEVERITIES.has(severity)) {
      return;
    }

    try {
      const text = [message.summary, message.detail]
        .filter((part): part is string => typeof part === 'string' && part.length > 0)
        .join(': ');

      // The severity prefix keeps "the server failed" distinguishable from
      // "the user was blocked by validation" — both matter, differently.
      this.telemetry.reportToast(`[${severity}] ${text || 'toast with no text'}`);
    } catch {
      // Reporting must never stop a toast from appearing. A user seeing nothing
      // because logging broke is strictly worse than a missing log line.
    }
  }
}
